<template>
  <Teleport to="body">
    <div
      v-if="isVisible"
      class="partner-contact-overlay fixed inset-0 bg-black/60 backdrop-blur-sm h-full w-full z-[200] flex items-end lg:items-center justify-center p-0 lg:p-4 overflow-hidden"
      @click="emit('close')"
    >
      <div
        class="partner-contact-sheet relative w-full max-w-md lg:max-w-4xl shadow-2xl rounded-t-2xl lg:rounded-2xl bg-white lg:my-4 overflow-hidden flex flex-col min-h-0 max-h-[92dvh] lg:max-h-[90vh]"
        @click.stop
      >
        <!-- Header -->
        <div class="flex items-center justify-between px-4 py-2.5 lg:py-4 lg:px-6 border-b border-gray-100 flex-shrink-0">
          <div class="min-w-0 pr-2">
            <h3 class="text-sm sm:text-base font-bold text-gray-900 truncate">{{ $t('partnerServices.modal.title') }}</h3>
            <p class="text-[11px] sm:text-xs text-gray-400 mt-0.5 hidden lg:block">{{ $t('partnerServices.modal.subtitle') }}</p>
          </div>
          <button
            @click="emit('close')"
            class="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Layout: servicios (desktop) + formulario -->
        <div class="flex flex-col flex-1 min-h-0 min-w-0 lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] lg:flex-none lg:max-h-[calc(90vh-52px)] lg:overflow-hidden">
          <!-- Columna izquierda: selector de servicios (solo desktop) -->
          <aside class="hidden lg:flex flex-col border-r border-gray-100 bg-gray-50/80 min-h-0">
            <div class="px-5 py-3 border-b border-gray-100">
              <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                {{ $t('partnerServices.modal.subtitle') }}
              </p>
            </div>
            <div class="flex-1 overflow-y-auto p-3 space-y-1.5">
              <button
                v-for="key in serviceOrder"
                :key="key"
                type="button"
                class="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border text-left transition-all"
                :class="selectedKey === key
                  ? 'border-indigo-500 bg-white shadow-sm ring-1 ring-indigo-500/20'
                  : 'border-transparent bg-white/60 hover:border-gray-200 hover:bg-white'"
                @click="selectedKey = key"
              >
                <div class="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" :class="serviceStyle(key).iconBg">
                  <svg class="w-4 h-4" :class="serviceStyle(key).iconColor" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="serviceStyle(key).iconPath" />
                  </svg>
                </div>

                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-1.5 flex-wrap">
                    <span class="text-sm font-medium" :class="selectedKey === key ? 'text-indigo-800' : 'text-gray-800'">
                      {{ $t(`partnerServices.services.${key}.title`) }}
                    </span>
                    <span
                      v-if="serviceStyle(key).badgeKey"
                      class="text-[10px] font-semibold px-1.5 py-0.5 rounded-full leading-none"
                      :class="serviceStyle(key).badgeClassLight"
                    >
                      {{ $t(`partnerServices.services.${key}.badge`) }}
                    </span>
                  </div>
                  <p class="text-xs text-gray-400 mt-0.5 line-clamp-2">
                    {{ $t(`partnerServices.services.${key}.shortDesc`) }}
                  </p>
                </div>

                <div
                  class="w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-all"
                  :class="selectedKey === key ? 'border-indigo-500 bg-indigo-500' : 'border-gray-300 bg-white'"
                >
                  <svg v-if="selectedKey === key" class="w-2.5 h-2.5 text-white" viewBox="0 0 10 10" fill="currentColor">
                    <path d="M3.5 7L1 4.5l1-1L3.5 5 8 0.5l1 1z" />
                  </svg>
                </div>
              </button>
            </div>
          </aside>

          <!-- Columna derecha: formulario -->
          <div class="flex flex-col flex-1 min-h-0 min-w-0 lg:overflow-hidden">
            <div class="partner-contact-scroll flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 pt-2 pb-1 lg:px-6 lg:pt-4 lg:pb-4">
            <form
              id="partner-inquiry-form"
              @submit.prevent="submitInquiry"
              class="space-y-2 lg:space-y-4"
            >
              <!-- Mobile: dropdown de servicio -->
              <div class="lg:hidden">
                <label class="block text-[11px] font-medium text-gray-600 mb-0.5">
                  {{ $t('partnerServices.modal.serviceRequired') }}
                </label>
                <select
                  v-model="selectedKey"
                  required
                  class="w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition bg-white"
                >
                  <option value="" disabled>{{ $t('partnerServices.modal.servicePlaceholder') }}</option>
                  <option v-for="key in serviceOrder" :key="key" :value="key">
                    {{ $t(`partnerServices.services.${key}.title`) }}
                  </option>
                </select>
              </div>

              <!-- Desktop: resumen del servicio seleccionado -->
              <div v-if="selectedKey" class="hidden lg:flex items-center gap-3 p-3 rounded-xl bg-indigo-50/80 border border-indigo-100">
                <div class="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" :class="activeService.iconBg">
                  <svg class="w-4 h-4" :class="activeService.iconColor" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="activeService.iconPath" />
                  </svg>
                </div>
                <div class="min-w-0">
                  <p class="text-sm font-semibold text-indigo-900">
                    {{ $t(`partnerServices.services.${selectedKey}.title`) }}
                  </p>
                  <p class="text-xs text-indigo-700/70 truncate">
                    {{ $t(`partnerServices.services.${selectedKey}.shortDesc`) }}
                  </p>
                </div>
              </div>

              <div class="grid grid-cols-2 gap-2 lg:gap-4">
                <div class="min-w-0">
                  <label class="block text-[11px] lg:text-xs font-medium text-gray-600 mb-0.5 lg:mb-1">
                    {{ $t('partnerServices.modal.form.nameRequired') }}
                  </label>
                  <input
                    v-model="form.name"
                    type="text"
                    required
                    autocomplete="name"
                    class="w-full px-2.5 py-1.5 lg:px-3.5 lg:py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
                    :placeholder="$t('partnerServices.modal.form.namePlaceholder')"
                  />
                </div>
                <div class="min-w-0">
                  <label class="block text-[11px] lg:text-xs font-medium text-gray-600 mb-0.5 lg:mb-1">
                    {{ $t('partnerServices.modal.form.company') }}
                  </label>
                  <input
                    v-model="form.company"
                    type="text"
                    autocomplete="organization"
                    class="w-full px-2.5 py-1.5 lg:px-3.5 lg:py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
                    :placeholder="$t('partnerServices.modal.form.companyPlaceholder')"
                  />
                </div>
              </div>

              <div>
                <label class="block text-[11px] lg:text-xs font-medium text-gray-600 mb-0.5 lg:mb-1">
                  {{ $t('partnerServices.modal.form.emailRequired') }}
                </label>
                <input
                  v-model="form.email"
                  type="email"
                  required
                  autocomplete="email"
                  inputmode="email"
                  class="w-full px-2.5 py-1.5 lg:px-3.5 lg:py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
                  :placeholder="$t('partnerServices.modal.form.emailPlaceholder')"
                />
              </div>

              <div>
                <label class="block text-[11px] lg:text-xs font-medium text-gray-600 mb-0.5 lg:mb-1">
                  {{ $t('partnerServices.modal.form.phone') }}
                </label>
                <input
                  v-model="form.phone"
                  type="tel"
                  autocomplete="tel"
                  inputmode="tel"
                  class="w-full px-2.5 py-1.5 lg:px-3.5 lg:py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
                  :placeholder="$t('partnerServices.modal.form.phonePlaceholder')"
                />
              </div>

              <div>
                <label class="block text-[11px] lg:text-xs font-medium text-gray-600 mb-0.5 lg:mb-1">
                  {{ $t('partnerServices.modal.form.message') }}
                </label>
                <textarea
                  v-model="form.message"
                  rows="2"
                  class="w-full px-2.5 py-1.5 lg:px-3.5 lg:py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition resize-none min-h-[3.25rem] lg:min-h-0"
                ></textarea>
              </div>

              <div class="hidden lg:flex gap-3 pt-1">
                <button
                  type="button"
                  @click="emit('close')"
                  class="flex-1 px-4 py-3 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 bg-white hover:bg-gray-50 hover:border-gray-300 transition-colors"
                >
                  {{ $t('partnerServices.modal.form.cancel') }}
                </button>
                <button
                  type="submit"
                  :disabled="isSubmitting"
                  class="flex-1 px-4 py-3 rounded-xl text-sm font-semibold bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <span v-if="isSubmitting">{{ $t('partnerServices.modal.form.submitting') }}</span>
                  <span v-else>{{ $t('partnerServices.modal.form.submit') }}</span>
                </button>
              </div>
            </form>
            </div>

            <!-- Mobile: barra fija pegada al contenido / borde inferior del sheet -->
            <div
              class="lg:hidden flex-shrink-0 border-t border-gray-100 bg-white px-4 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] shadow-[0_-8px_24px_rgba(15,23,42,0.08)]"
            >
              <button
                type="submit"
                form="partner-inquiry-form"
                :disabled="isSubmitting"
                class="w-full px-4 py-2.5 rounded-xl text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700 active:bg-indigo-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <span v-if="isSubmitting">{{ $t('partnerServices.modal.form.submitting') }}</span>
                <span v-else>{{ $t('partnerServices.modal.form.submit') }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>

  <!-- Toast de éxito -->
  <Teleport to="body">
    <div
      v-if="showSuccess"
      class="fixed bottom-4 right-4 max-w-sm w-full bg-white shadow-lg rounded-xl pointer-events-auto ring-1 ring-black/5 z-[300] p-4"
    >
      <div class="flex items-start gap-3">
        <svg class="h-6 w-6 text-green-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <div>
          <p class="text-sm font-semibold text-gray-900">{{ $t('partnerServices.modal.success.title') }}</p>
          <p class="text-sm text-gray-500 mt-0.5">{{ $t('partnerServices.modal.success.message') }}</p>
        </div>
        <button @click="showSuccess = false" class="ml-auto text-gray-400 hover:text-gray-600 flex-shrink-0">
          <svg class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
          </svg>
        </button>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { trackInteraction } from '@/utils/trackInteraction'
import { PARTNER_SERVICE_ORDER, getPartnerServiceStyle } from '@/config/partnerServiceCatalog'

const { t } = useI18n()

const props = defineProps({
  isVisible: { type: Boolean, default: false },
  initialService: { type: String, default: 'marketplace' }
})

const emit = defineEmits(['close'])

const selectedKey = ref(props.initialService)
const isSubmitting = ref(false)
const showSuccess = ref(false)
const form = ref({ name: '', company: '', email: '', phone: '', message: '' })

const serviceOrder = PARTNER_SERVICE_ORDER

function serviceStyle(key) {
  return getPartnerServiceStyle(key)
}

watch(() => props.isVisible, (val) => {
  if (typeof document === 'undefined') return
  if (val) {
    selectedKey.value = props.initialService || 'marketplace'
    form.value = { name: '', company: '', email: '', phone: '', message: '' }
    trackInteraction('partner_modal_open', { service: selectedKey.value })
    document.documentElement.classList.add('partner-contact-modal-open')
  } else {
    document.documentElement.classList.remove('partner-contact-modal-open')
  }
})

const activeService = computed(() => {
  const style = getPartnerServiceStyle(selectedKey.value)
  return {
    iconBg: style.iconBg,
    iconColor: style.iconColor,
    iconPath: style.iconPath,
  }
})

async function submitInquiry() {
  if (isSubmitting.value) return
  try {
    isSubmitting.value = true
    const response = await fetch('/api/email/send-partner-inquiry', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        serviceType: selectedKey.value,
        serviceName: t(`partnerServices.services.${selectedKey.value}.title`),
        name: form.value.name,
        company: form.value.company,
        email: form.value.email,
        phone: form.value.phone,
        message: form.value.message
      })
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      throw new Error(err.message || `Error del servidor (${response.status})`)
    }
    trackInteraction('partner_inquiry_sent', { service: selectedKey.value })
    emit('close')
    showSuccess.value = true
    setTimeout(() => { showSuccess.value = false }, 5000)
  } catch (error) {
    console.error('Error enviando solicitud:', error)
    alert('Error al enviar la solicitud. Por favor, inténtalo de nuevo.')
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style scoped>
:global(html.partner-contact-modal-open) {
  overflow: hidden;
}

@media (max-width: 1023px) {
  .partner-contact-sheet {
    width: 100%;
    max-height: min(92dvh, 100%);
  }

  .partner-contact-scroll {
    flex: 1 1 auto;
    min-height: 0;
    -webkit-overflow-scrolling: touch;
  }
}

@supports not (height: 92dvh) {
  @media (max-width: 1023px) {
    .partner-contact-sheet {
      max-height: 92vh;
    }
  }
}
</style>
